import { useEffect, useMemo, useState } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import {
  AstuteCustomRateSyncStatus,
  Job,
  SupportedIntegrationPlatform,
} from '@core/generated/gql-types';
import {
  AstuteOccupationLibraryListItem,
  formatAstuteOccupationLibraryLabel,
  integrationActions,
} from '@core/integration/integration.actions';
import { useIntegrationStore } from '@core/integration/useIntegrationStore';
import { useJobStore } from '@core/job/useJobStore';
import { actionPrimaryButtonClassName } from '@core/theme/actionTheme.util';
import ActionListboxField from '@/components/shared/ActionListboxField';

interface AstuteRateSyncSectionProps {
  job: Job;
  onJobUpdated: (job: Job) => void;
}

// Selector fallback must be stable, otherwise every store write re-renders.
const NO_OCCUPATION_LIBRARIES: AstuteOccupationLibraryListItem[] = [];

// Astute caps billers_ponum at 32 characters.
const PO_NUMBER_MAX_LENGTH = 32;

const STATUS_LABELS: Record<AstuteCustomRateSyncStatus, string> = {
  [AstuteCustomRateSyncStatus.NotSynced]: 'Not synced to Astute',
  [AstuteCustomRateSyncStatus.Stale]: 'Custom rates changed — not synced',
  [AstuteCustomRateSyncStatus.Synced]: 'Synced to Astute',
  [AstuteCustomRateSyncStatus.Failed]: 'Sync failed',
};

const STATUS_COLOURS: Record<AstuteCustomRateSyncStatus, string> = {
  [AstuteCustomRateSyncStatus.NotSynced]: 'bg-gray-400',
  [AstuteCustomRateSyncStatus.Stale]: 'bg-amber-500',
  [AstuteCustomRateSyncStatus.Synced]: 'bg-green-500',
  [AstuteCustomRateSyncStatus.Failed]: 'bg-red-500',
};

const AstuteRateSyncSection = ({
  job,
  onJobUpdated,
}: AstuteRateSyncSectionProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoadingLibraries, setIsLoadingLibraries] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [selectedOccupationLibraryOid, setSelectedOccupationLibraryOid] =
    useState('');
  const [poNumberInput, setPoNumberInput] = useState('');
  const fetchJobById = useJobStore((state) => state.fetchJobById);
  const ensureIntegrationConnections = useIntegrationStore(
    (state) => state.ensureIntegrationConnections,
  );
  const ensureAstuteOccupationLibraries = useIntegrationStore(
    (state) => state.ensureAstuteOccupationLibraries,
  );
  const refreshAstuteOccupationLibraries = useIntegrationStore(
    (state) => state.refreshAstuteOccupationLibraries,
  );
  const occupationLibraries = useIntegrationStore(
    (state) =>
      state.astuteOccupationLibrariesByTenantId[job.tenantId] ??
      NO_OCCUPATION_LIBRARIES,
  );
  const integrationStatus = useIntegrationStore(
    (state) => state.integrationStatus,
  );
  const loadedTenantId = useIntegrationStore((state) => state.loadedTenantId);
  const astuteIntegration = useIntegrationStore((state) =>
    state.integrationConnections.find(
      (integrationConnection) =>
        state.integrationPlatformByIntegrationId[
          integrationConnection.integrationId
        ] === SupportedIntegrationPlatform.Astute,
    ),
  );

  useEffect(() => {
    void ensureIntegrationConnections(job.tenantId);
  }, [ensureIntegrationConnections, job.tenantId]);

  const occupationLibraryOptions = useMemo(
    () =>
      occupationLibraries
        .map((occupationLibrary) => ({
          label: formatAstuteOccupationLibraryLabel(occupationLibrary),
          value: occupationLibrary.OL_OID,
        }))
        .sort((leftOption, rightOption) =>
          leftOption.label.localeCompare(rightOption.label),
        ),
    [occupationLibraries],
  );

  const syncStatus =
    job.astuteCustomRateSyncStatus ?? AstuteCustomRateSyncStatus.NotSynced;
  const isSynced = syncStatus === AstuteCustomRateSyncStatus.Synced;
  const updateButtonLabel = !job.astuteOccupationLibraryOid
    ? 'Sync to Astute'
    : syncStatus === AstuteCustomRateSyncStatus.Stale
      ? 'Resync'
      : 'Edit';
  const isAstuteEnabled =
    loadedTenantId === job.tenantId &&
    integrationStatus === 'ready' &&
    astuteIntegration?.integrationEnabled === true;

  const formattedSyncedAt = useMemo(() => {
    if (!job.astuteCustomRateSyncedAt) return null;

    const syncedAt = parseISO(job.astuteCustomRateSyncedAt);
    return isValid(syncedAt) ? format(syncedAt, 'd MMM yyyy, h:mm a') : null;
  }, [job.astuteCustomRateSyncedAt]);

  // The job only stores the OID, so the name comes from the tenant's library list.
  useEffect(() => {
    if (!isAstuteEnabled || !job.astuteOccupationLibraryOid) {
      return;
    }

    void ensureAstuteOccupationLibraries(job.tenantId).catch((error) => {
      console.error('Unable to load Astute occupation libraries', error);
    });
  }, [
    ensureAstuteOccupationLibraries,
    isAstuteEnabled,
    job.astuteOccupationLibraryOid,
    job.tenantId,
  ]);

  const selectedOccupationLibraryLabel = useMemo(() => {
    if (!job.astuteOccupationLibraryOid) return null;

    const matchedOccupationLibrary = occupationLibraries.find(
      (occupationLibrary) =>
        occupationLibrary.OL_OID === job.astuteOccupationLibraryOid,
    );

    return matchedOccupationLibrary
      ? formatAstuteOccupationLibraryLabel(matchedOccupationLibrary)
      : job.astuteOccupationLibraryOid;
  }, [job.astuteOccupationLibraryOid, occupationLibraries]);

  const handleUpdate = async () => {
    setIsUpdating(true);
    setIsLoadingLibraries(true);
    setSelectedOccupationLibraryOid(job.astuteOccupationLibraryOid ?? '');
    setPoNumberInput(job.poNumber ?? '');

    try {
      await refreshAstuteOccupationLibraries(job.tenantId);
    } catch (error) {
      setIsUpdating(false);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Unable to load Astute occupation libraries',
      );
    } finally {
      setIsLoadingLibraries(false);
    }
  };

  const handleSync = async () => {
    if (!selectedOccupationLibraryOid) {
      return;
    }

    setIsSyncing(true);
    try {
      const updatedJob = await integrationActions.syncJobAssignment(
        job.id,
        selectedOccupationLibraryOid,
        poNumberInput.trim(),
      );
      onJobUpdated(updatedJob);
      setIsUpdating(false);
      toast.success('Custom rates synced to Astute');
    } catch (error) {
      // The failed status lives on the job, but a failed refetch must not swallow the toast.
      try {
        const refreshedJob = await fetchJobById(job.id);
        if (refreshedJob) {
          onJobUpdated(refreshedJob);
        }
      } catch (refreshError) {
        console.error('Unable to refresh job after Astute sync', refreshError);
      }
      toast.error(
        error instanceof Error
          ? error.message
          : 'Unable to sync custom rates to Astute',
      );
    } finally {
      setIsSyncing(false);
    }
  };

  if (!isAstuteEnabled) {
    return null;
  }

  return (
    <div className="mb-6 rounded-lg bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Astute ATS</h2>
          <div className="mt-3 flex items-center gap-2">
            <span
              className={`h-2.5 w-2.5 rounded-full ${STATUS_COLOURS[syncStatus]}`}
            />
            <span className="text-sm text-gray-700">
              {STATUS_LABELS[syncStatus]}
            </span>
          </div>
          {selectedOccupationLibraryLabel && (
            <p className="mt-1 text-sm text-gray-700">
              Occupation library:{' '}
              <span className="font-medium">
                {selectedOccupationLibraryLabel}
              </span>
            </p>
          )}
          {job.poNumber && (
            <p className="mt-1 text-sm text-gray-700">
              PO number: <span className="font-medium">{job.poNumber}</span>
            </p>
          )}
          {isSynced && formattedSyncedAt && (
            <p className="mt-1 text-sm text-gray-500">
              Last synced {formattedSyncedAt}
            </p>
          )}
          {syncStatus === AstuteCustomRateSyncStatus.Failed &&
            job.astuteCustomRateSyncError && (
              <p className="mt-1 text-sm text-red-600">
                {job.astuteCustomRateSyncError}
              </p>
            )}
        </div>
        {!isUpdating && (
          <button
            type="button"
            className={`rounded-md px-4 py-2 text-sm font-medium ${actionPrimaryButtonClassName}`}
            onClick={() => void handleUpdate()}
          >
            {updateButtonLabel}
          </button>
        )}
      </div>

      {isUpdating && (
        <div className="mt-5 border-t border-gray-200 pt-5">
          <label
            htmlFor="astute-occupation-library"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            Occupation library
          </label>
          {isLoadingLibraries ? (
            <p className="text-sm text-gray-500">
              Loading occupation libraries...
            </p>
          ) : (
            <ActionListboxField
              id="astute-occupation-library"
              value={selectedOccupationLibraryOid}
              onChange={setSelectedOccupationLibraryOid}
              options={occupationLibraryOptions}
              searchable
              searchPlaceholder="Search occupation libraries..."
              noMatchesLabel="No occupation libraries match"
              placeholder={
                occupationLibraryOptions.length
                  ? 'Select an occupation library'
                  : 'No occupation libraries available'
              }
            />
          )}
          <label
            htmlFor="astute-po-number"
            className="mt-4 mb-2 block text-sm font-medium text-gray-700"
          >
            PO number{' '}
            <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <input
            id="astute-po-number"
            type="text"
            value={poNumberInput}
            onChange={(event) => setPoNumberInput(event.target.value)}
            maxLength={PO_NUMBER_MAX_LENGTH}
            placeholder="Enter a PO number"
            disabled={isSyncing}
            className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
          />
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={isSyncing}
              onClick={() => setIsUpdating(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`rounded-md px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50 ${actionPrimaryButtonClassName}`}
              disabled={
                isLoadingLibraries || isSyncing || !selectedOccupationLibraryOid
              }
              onClick={() => void handleSync()}
            >
              {isSyncing ? 'Syncing...' : 'Sync to Astute'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AstuteRateSyncSection;
